'use client';

import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Camera, Loader2, X, Image as ImageIcon } from 'lucide-react';
import { postMultipartImageUpload } from '@/lib/vertex-upload';
import { toast } from 'sonner';
import { ImageCropper } from '@/components/ui/image-cropper';
import { ImageFileInput } from '@/components/forms/image-file-input';
import { FIELD_CHROME } from '@/lib/field-chrome';

interface EventBannerUploadProps {
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  freeForm?: boolean;
}

export function EventBannerUpload({ value, onChange, disabled, freeForm }: EventBannerUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [converting, setConverting] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadFile = async (file: Blob, fileName: string) => {
    setIsUploading(true);

    const formData = new FormData();
    formData.append('file', file, fileName);
    formData.append('folder', 'event-banners');

    try {
      const { url } = await postMultipartImageUpload(formData);
      onChange(url);
      toast.success('Image uploaded successfully');
      setSelectedImage(null); // Close cropper
    } catch (error) {
      console.error(error);
      toast.error('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handlePrepared = async (file: File) => {
    await uploadFile(file, file.name);
  };

  const handlePreparedDataUrl = (dataUrl: string) => {
    setSelectedImage(dataUrl);
  };

  const onCropComplete = async (croppedBlob: Blob) => {
    await uploadFile(croppedBlob, 'banner.jpg');
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    onChange('');
  };

  return (
    <div className="w-full">
      {selectedImage && (
        <ImageCropper
          imageSrc={selectedImage}
          onCropComplete={onCropComplete}
          onCancel={() => {
            setSelectedImage(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
          }}
          aspect={16 / 9}
        />
      )}

      <div
        className={`relative w-full aspect-video rounded-xl ${FIELD_CHROME.solid.base} flex flex-col items-center justify-center cursor-pointer ${value ? 'border-none shadow-none dark:shadow-none hover:shadow-none dark:hover:shadow-none' : ''}`}
        onClick={() => !disabled && !isUploading && !converting && !value && fileInputRef.current?.click()}
      >
        {value ? (
          <>
            <img src={value} alt="Event Banner" className="w-full h-full object-cover rounded-xl" />
            {!disabled && !isUploading && !converting && (
              <div className="absolute top-2 right-2 flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  <Camera className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(e);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 text-muted-foreground p-4 text-center">
            {isUploading || converting ? (
              <Loader2 className="h-8 w-8 animate-spin" />
            ) : (
              <ImageIcon className="h-10 w-10" />
            )}
            <div className="text-sm font-medium">
              {converting
                ? 'Converting photo...'
                : isUploading
                  ? 'Uploading...'
                  : `Click to upload ${freeForm ? 'image' : 'banner'}`}
            </div>
            {!freeForm && <div className="text-xs">16:9 aspect ratio recommended. Max 5MB.</div>}
            {freeForm && <div className="text-xs">Any aspect ratio is allowed. Max 5MB.</div>}
          </div>
        )}

        <ImageFileInput
          ref={fileInputRef}
          className="hidden"
          accept="image/*"
          onPreparingChange={setConverting}
          onPrepared={freeForm ? handlePrepared : undefined}
          onPreparedDataUrl={freeForm ? undefined : handlePreparedDataUrl}
          disabled={disabled || isUploading || converting}
        />
      </div>
    </div>
  );
}
